/**
 * MedicationNameField.tsx
 *
 * The medication form's Name input, with name suggestions as you type
 * (useMedicationSuggestions: NLM RxTerms for medications, the NIH supplement
 * label database for supplements). The field stays free text: suggestions
 * only help, and a failed lookup simply shows none.
 *
 * Keyboard: Arrow keys move through the list, Enter picks, Escape closes.
 */
import { useId, useState } from 'react';
import { Input } from '@/components/ui';
import { useMedicationSuggestions } from '@/hooks/useMedicationSuggestions';
import type { MedicationKind, MedicationSuggestion } from '@/lib/medicationSuggestions';

const KIND_LABELS: Record<MedicationKind, string> = {
  medication: 'Medication',
  supplement: 'Supplement',
  peptide: 'Peptide',
};

interface MedicationNameFieldProps {
  kind: MedicationKind;
  value: string;
  /** Typed text. */
  onChange: (value: string) => void;
  /** A suggestion was chosen. */
  onPick: (suggestion: MedicationSuggestion) => void;
  error?: string;
  disabled?: boolean;
}

export function MedicationNameField({ kind, value, onChange, onPick, error, disabled }: MedicationNameFieldProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const suggestions = useMedicationSuggestions(kind, open && !disabled ? value : '');
  const showList = open && !disabled && suggestions.length > 0;

  function pick(suggestion: MedicationSuggestion) {
    onPick(suggestion);
    setOpen(false);
    setHighlight(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <Input
        label={KIND_LABELS[kind]}
        required
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        error={error}
        disabled={disabled}
        maxLength={255}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && highlight >= 0 ? `${listId}-${highlight}` : undefined}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.label}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={`cursor-pointer px-3 py-2 ${i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              // mousedown, not click: the input's blur would close the list first.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
